import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { purgeStoredState } from 'redux-persist';
import { resetStoreAction } from 'state/commonActions';
import { clearAllNotifications } from 'state/slices/notificationsSlice';
import { persistConfig } from 'state/store';

import { postUserLogout } from 'services';
import handleError from 'utils/handleError';
import { IN_IFRAME } from 'utils/iframe';
import { requestSdkClose } from 'utils/sdkClose';
import { readEntrataRedirectLinkFromSession } from 'utils/viewerLaunchContextSession';

const useLogout = () => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { orgId, propertyId } = useParams();

  const clearState = useCallback(() => {
    dispatch(clearAllNotifications());
    queryClient.clear();
    dispatch(resetStoreAction());
    purgeStoredState(persistConfig);
    sessionStorage.removeItem('viewer.applicant');
  }, [dispatch, queryClient]);

  const clearStorageOnly = useCallback(() => {
    queryClient.clear();
    purgeStoredState(persistConfig);
    sessionStorage.removeItem('viewer.applicant');
  }, [queryClient]);

  const redirectAfterLogout = useCallback(() => {
    if (IN_IFRAME) {
      clearState();
      requestSdkClose();
    } else {
      const redirectLink = readEntrataRedirectLinkFromSession();
      if (redirectLink) {
        clearStorageOnly();
        window.location.href = redirectLink;
      } else {
        clearState();
        navigate(`/${orgId}/${propertyId}${location.search}`, { replace: true });
      }
    }
  }, [clearState, clearStorageOnly, navigate, orgId, propertyId, location.search]);

  const handleLogout = useCallback(async () => {
    try {
      setIsLoading(true);
      await postUserLogout();
      redirectAfterLogout();
    } catch (error) {
      redirectAfterLogout();
      handleError(`Form Viewer - Error during logout: ${error}`, `Error during logout`);
    } finally {
      setIsLoading(false);
    }
  }, [redirectAfterLogout]);

  return { handleLogout, isLoading };
};

export default useLogout;
